import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, PlusCircle, Search, X } from "lucide-react";

import {
  archiveClient,
  createClient,
  createClientProject,
  createProject,
  getClientById,
  getClientProjects,
  getAuditLogs,
  getClients,
  pauseClient,
  resumeClient,
} from "@/lib/api";
import { canCreatePortfolio as canCreatePortfolioByOrg } from "@/lib/org";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "@/components/ui/sonner";

const RETAINER_SERVICE_OPTIONS = ["SEO", "Social Media", "Digital PR / Founder Branding", "Performance Marketing", "AMC", "GMB"];
const FIXED_SERVICE_OPTIONS = ["Website/Software", "Landing Page", "PR", "Content", "Newsletter/Emails", "Photoshoot/VideoShoot", "Other"];

const clientPauseReasons = [
  "Strategic hold",
  "Awaiting documents from client",
  "Payment delay",
  "Internal capacity",
  "Campaign restructuring",
  "Other",
];

const clientArchiveReasons = [
  "Contract ended",
  "Client churn",
  "Merged account",
  "Non-payment",
  "Other",
];

const clientSchema = z.object({
  name: z.string().min(2, "Client name is required"),
  industry: z.string().min(2, "Industry required"),
  currency: z.enum(["INR", "USD", "GBP", "EUR"]),
  start_date: z.string().min(4, "Start date required"),
  contact_name: z.string().optional(),
  contact_email: z.string().email("Enter a valid contact email").optional().or(z.literal("")),
});

const projectSchema = z
  .object({
    client_id: z.string().min(1, "Select a client"),
    name: z.string().min(2, "Project name is required"),
    service_type: z.string().min(2, "Service type required"),
    project_admin_email: z.string().email("Select a valid project admin"),
    project_type: z.enum(["fixed", "retainer"]),
    visibility_scope: z.enum(["workspace_only", "shared_both"]),
    project_deadline: z.string().optional(),
    retainer_start_date: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.project_type === "fixed" && !values.project_deadline) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Project deadline is required for fixed projects",
        path: ["project_deadline"],
      });
    }
    if (values.project_type === "retainer" && !values.retainer_start_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Retainer start date is required for retainer projects",
        path: ["retainer_start_date"],
      });
    }
  });

const getCurrencySymbol = (currency) => {
  if (currency === "USD") return "$";
  if (currency === "GBP") return "£";
  if (currency === "EUR") return "€";
  return "₹";
};

const healthBand = (score) => {
  if (score >= 80) return "healthy";
  if (score >= 65) return "watch";
  if (score >= 50) return "at_risk";
  return "critical";
};

const healthChipClasses = {
  healthy: "chip chip-ok",
  watch: "chip chip-warn",
  at_risk: "chip chip-risk",
  critical: "chip chip-crit",
};

const healthDotClasses = {
  healthy: "dot dot-ok",
  watch: "dot dot-warn",
  at_risk: "dot dot-risk",
  critical: "dot dot-crit",
};

const healthLabel = {
  healthy: "Healthy",
  watch: "Watch",
  at_risk: "At risk",
  critical: "Critical",
};

const chipForService = (service) => {
  const normalized = String(service || "").toLowerCase();
  if (normalized.includes("social")) return "chip chip-violet";
  if (normalized.includes("pr")) return "chip chip-ok";
  if (normalized.includes("performance")) return "chip chip-risk";
  if (normalized.includes("gmb")) return "chip chip-warn";
  if (normalized.includes("amc")) return "chip chip-mute";
  return "chip chip-brand";
};

const chipForBillingType = (billingType) => {
  return String(billingType || "retainer").toLowerCase() === "fixed" ? "chip chip-violet" : "chip chip-brand";
};

const initialsFromEmail = (email = "") => {
  const local = String(email).split("@")[0] || "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (!parts.length) return "NA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatDateLabel = (value) => {
  if (!value) return "—";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return value;
  return dateValue.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const isHealthMatch = (status, selectedFilter) => {
  if (!selectedFilter) return true;
  if (selectedFilter === "crit") return status === "critical";
  if (selectedFilter === "risk") return status === "at_risk";
  if (selectedFilter === "warn") return status === "watch";
  if (selectedFilter === "ok") return status === "healthy";
  return true;
};

const ringStrokeColor = (score) => {
  const band = healthBand(score);
  if (band === "healthy") return "#10B981";
  if (band === "watch") return "#F59E0B";
  if (band === "at_risk") return "#F97316";
  return "#EF4444";
};

const buildClientDetailRoute = (client) => {
  const clientId = String(client?.id || "").trim();
  if (!clientId) return "";
  return `/project/${encodeURIComponent(clientId)}/update-client-view`;
};

export default function ClientsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const canCreatePortfolio = canCreatePortfolioByOrg(user);

  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [scopedClientId, setScopedClientId] = useState("");
  const [lockProjectClientField, setLockProjectClientField] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    service: "",
    health: "",
    status: "all",
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeClientId, setActiveClientId] = useState("");
  const [activeClient, setActiveClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientAuditRows, setClientAuditRows] = useState([]);
  const [isClientAuditExpanded, setIsClientAuditExpanded] = useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [clientPauseModal, setClientPauseModal] = useState({ open: false, client: null });
  const [clientArchiveModal, setClientArchiveModal] = useState({ open: false, client: null });
  const [clientPauseForm, setClientPauseForm] = useState({ reason: "", notes: "" });
  const [clientArchiveForm, setClientArchiveForm] = useState({ reason: "", confirm_name: "" });
  const [isLifecycleSaving, setIsLifecycleSaving] = useState(false);

  const openClientDetailedView = (client) => {
    const route = buildClientDetailRoute(client);
    if (!route) {
      toast.error("Unable to open client detailed view");
      return;
    }
    navigate(route);
  };

  const clientForm = useForm({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: "",
      industry: "",
      currency: "INR",
      start_date: "",
      contact_name: "",
      contact_email: "",
    },
  });

  const projectForm = useForm({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      client_id: "",
      name: "",
      service_type: "",
      project_admin_email: user.email,
      project_type: "retainer",
      visibility_scope: "workspace_only",
      project_deadline: "",
      retainer_start_date: "",
    },
  });
  const selectedProjectType = projectForm.watch("project_type");
  const selectedServiceOptions = useMemo(
    () => (selectedProjectType === "fixed" ? FIXED_SERVICE_OPTIONS : RETAINER_SERVICE_OPTIONS),
    [selectedProjectType],
  );

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const clientsResponse = await getClients();
      setClients(clientsResponse);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openClientId = params.get("open_client");
    if (!openClientId || isLoading) return;

    const exists = clients.some((client) => client.id === openClientId);
    if (!exists) return;

    if (activeClientId !== openClientId) {
      openClientDrawer(openClientId);
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("open_client");
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [activeClientId, clients, isLoading, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (createProjectOpen && !projectForm.getValues("project_admin_email")) {
      projectForm.setValue("project_admin_email", user.email);
    }
  }, [createProjectOpen, projectForm, user.email]);

  useEffect(() => {
    const currentService = projectForm.getValues("service_type");
    if (!currentService) return;
    if (!selectedServiceOptions.includes(currentService)) {
      projectForm.setValue("service_type", "", { shouldValidate: true });
    }
  }, [projectForm, selectedServiceOptions]);

  const openClientDrawer = async (clientId) => {
    setIsDrawerOpen(true);
    setActiveClientId(clientId);
    setIsDrawerLoading(true);
    setIsClientAuditExpanded(false);
    try {
      const [clientDetails, projectRows] = await Promise.all([getClientById(clientId), getClientProjects(clientId)]);
      setActiveClient(clientDetails);
      setClientProjects(projectRows);
      if (user.role === "super_admin") {
        try {
          const auditResponse = await getAuditLogs({
            entity_type: "client",
            entity_id: clientId,
            page: 1,
            limit: 10,
          });
          setClientAuditRows(auditResponse?.rows || []);
        } catch (_error) {
          setClientAuditRows([]);
        }
      } else {
        setClientAuditRows([]);
      }
    } catch (error) {
      toast.error(error.message);
      setIsDrawerOpen(false);
      setActiveClientId("");
    } finally {
      setIsDrawerLoading(false);
    }
  };

  const closeClientDrawer = () => {
    setIsDrawerOpen(false);
    setActiveClientId("");
    setActiveClient(null);
    setClientProjects([]);
    setClientAuditRows([]);
    setIsClientAuditExpanded(false);
    setIsDrawerLoading(false);
  };

  const openProjectDialog = ({ clientId = "", lockClient = false } = {}) => {
    setScopedClientId(clientId);
    setLockProjectClientField(lockClient);
    setCreateProjectOpen(true);
    if (clientId) {
      projectForm.setValue("client_id", clientId);
    } else {
      projectForm.setValue("client_id", "");
    }
  };

  const closeProjectDialog = () => {
    setCreateProjectOpen(false);
    setScopedClientId("");
    setLockProjectClientField(false);
    projectForm.reset({
      client_id: "",
      name: "",
      service_type: "",
      project_admin_email: user.email,
      project_type: "retainer",
      visibility_scope: "workspace_only",
      project_deadline: "",
      retainer_start_date: "",
    });
  };

  const filteredClients = useMemo(() => {
    const searchNeedle = filters.search.trim().toLowerCase();
    return clients.filter((client) => {
      const searchableText = `${client.name} ${client.industry}`.toLowerCase();
      if (searchNeedle && !searchableText.includes(searchNeedle)) return false;

      if (filters.service) {
        const services = Array.isArray(client.services_assigned) ? client.services_assigned : [];
        if (!services.some((item) => item === filters.service)) return false;
      }

      if (!isHealthMatch(client.worst_health_status, filters.health)) return false;
      if (filters.status && filters.status !== "all") {
        const status = String(client.status || "active").toLowerCase();
        if (status !== filters.status) return false;
      }
      return true;
    });
  }, [clients, filters.health, filters.search, filters.service, filters.status]);

  const activeClients = useMemo(
    () => clients.filter((client) => String(client.status || "active").toLowerCase() === "active"),
    [clients],
  );

  const clientsByStatus = useMemo(() => {
    const grouped = { active: [], paused: [], archived: [] };
    filteredClients.forEach((client) => {
      const status = String(client.status || "active").toLowerCase();
      if (grouped[status]) grouped[status].push(client);
    });
    return grouped;
  }, [filteredClients]);

  const stats = useMemo(
    () => ({
      totalClients: clients.length,
      activeProjects: clients.reduce((sum, client) => sum + Number(client.active_projects || 0), 0),
      overdueTasks: clients.reduce((sum, client) => sum + Number(client.overdue_tasks || 0), 0),
      healthyClients: clients.filter((client) => client.average_health_status === "healthy").length,
    }),
    [clients],
  );

  const filteredSummary = useMemo(() => {
    const totalProjects = filteredClients.reduce((sum, client) => sum + Number(client.project_count || 0), 0);
    return `${filteredClients.length} clients · ${totalProjects} projects`;
  }, [filteredClients]);

  const drawerBillingBreakdownRows = useMemo(
    () => (clientProjects || []).map((project) => ({
      id: project.id,
      name: project.name,
      status: String(project.status || "active").toLowerCase(),
      value: Number(project.project_value_monthly || 0),
      service_type: project.service_type || "Marketing",
    })),
    [clientProjects],
  );

  const drawerBillingBreakdownTotal = useMemo(
    () => drawerBillingBreakdownRows.reduce((sum, row) => sum + Number(row.value || 0), 0),
    [drawerBillingBreakdownRows],
  );

  const onCreateClient = async (values) => {
    try {
      await createClient({
        ...values,
        contact_name: values.contact_name || null,
        contact_email: values.contact_email || null,
      });
      toast.success("Client created");
      setCreateClientOpen(false);
      clientForm.reset({
        name: "",
        industry: "",
        currency: "INR",
        start_date: "",
        contact_name: "",
        contact_email: "",
      });
      await loadClients();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onCreateProject = async (values) => {
    const selectedClientId = lockProjectClientField && scopedClientId ? scopedClientId : values.client_id;
    const selectedClient = clients.find((client) => client.id === selectedClientId);
    if (selectedClient && String(selectedClient.status || "active").toLowerCase() !== "active") {
      toast.error("Projects can only be created for active clients.");
      return;
    }

    const payload = {
      name: values.name,
      service_type: values.service_type,
      description: "",
      project_admin_email: user.email,
      member_emails: [],
      billing_type: values.project_type,
      project_type: values.project_type,
      visibility_scope: values.visibility_scope,
      project_deadline: values.project_type === "fixed" ? values.project_deadline : null,
      retainer_start_date: values.project_type === "retainer" ? values.retainer_start_date : null,
    };

    try {
      if (lockProjectClientField && scopedClientId) {
        await createClientProject(scopedClientId, payload);
      } else {
        await createProject({ ...payload, client_id: values.client_id });
      }
      toast.success("Project created");
      closeProjectDialog();
      await loadClients();
      if (isDrawerOpen && activeClientId) {
        await openClientDrawer(activeClientId);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openPauseClientModal = (event, client) => {
    event.stopPropagation();
    if (user.role !== "super_admin") {
      toast.error("Only super admin can manage client status");
      return;
    }
    setClientPauseForm({ reason: "", notes: "" });
    setClientPauseModal({ open: true, client });
  };

  const openArchiveClientModal = (event, client) => {
    event.stopPropagation();
    if (user.role !== "super_admin") {
      toast.error("Only super admin can manage client status");
      return;
    }
    setClientArchiveForm({ reason: "", confirm_name: "" });
    setClientArchiveModal({ open: true, client });
  };

  const handleResumeClient = async (event, client) => {
    event.stopPropagation();
    if (user.role !== "super_admin") {
      toast.error("Only super admin can manage client status");
      return;
    }
    try {
      await resumeClient(client.id, { reason: "Resumed from clients view" });
      toast.success("Client resumed");
      await loadClients();
      if (isDrawerOpen && activeClientId) await openClientDrawer(activeClientId);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmPauseClient = async () => {
    if (!clientPauseModal.client) return;
    if (!clientPauseForm.reason) {
      toast.error("Please select a pause reason");
      return;
    }
    setIsLifecycleSaving(true);
    try {
      const reason = clientPauseForm.notes ? `${clientPauseForm.reason} — ${clientPauseForm.notes}` : clientPauseForm.reason;
      await pauseClient(clientPauseModal.client.id, { reason });
      toast.success("Client paused");
      setClientPauseModal({ open: false, client: null });
      await loadClients();
      if (isDrawerOpen && activeClientId) await openClientDrawer(activeClientId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLifecycleSaving(false);
    }
  };

  const archiveClientNameMatched = useMemo(() => {
    const client = clientArchiveModal.client;
    if (!client) return false;
    return Boolean(clientArchiveForm.reason) && String(clientArchiveForm.confirm_name || "").trim().toLowerCase() === String(client.name || "").trim().toLowerCase();
  }, [clientArchiveForm.confirm_name, clientArchiveForm.reason, clientArchiveModal.client]);

  const confirmArchiveClient = async () => {
    if (!clientArchiveModal.client || !archiveClientNameMatched) return;
    setIsLifecycleSaving(true);
    try {
      await archiveClient(clientArchiveModal.client.id, {
        reason: clientArchiveForm.reason,
        confirm_name: clientArchiveForm.confirm_name,
      });
      toast.success("Client archived");
      setClientArchiveModal({ open: false, client: null });
      await loadClients();
      if (isDrawerOpen && activeClientId) await openClientDrawer(activeClientId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLifecycleSaving(false);
    }
  };

  return (
    <AppShell user={user} onLogout={onLogout} topbarSearchPlaceholder="Search tasks, projects...">
      <section className="space-y-5" data-testid="clients-page-root">
        <header className="page-header" style={{ marginBottom: 20 }} data-testid="clients-page-header">
          <div>
            <h1 className="page-title" data-testid="clients-page-title">Clients &amp; Projects</h1>
            <p className="page-sub" data-testid="clients-page-subtitle">Click any client to see all their projects</p>
          </div>
          <div className="page-actions" data-testid="clients-page-header-actions">
            {canCreatePortfolio ? (
              <>
                <Dialog open={createClientOpen} onOpenChange={setCreateClientOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="open-create-client-dialog-button">+ Add Client</Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[92vh] overflow-y-auto p-0 sm:max-w-[920px]" data-testid="create-client-dialog">
                    <DialogHeader className="border-b border-[var(--paper-200)] px-8 py-6">
                      <DialogTitle className="text-[52px] leading-none tracking-[-0.02em]" data-testid="create-client-title">Create Client</DialogTitle>
                      <DialogDescription className="text-[22px] text-[var(--mute-500)]" data-testid="create-client-description">
                        Clients represent company accounts your agency serves. Monthly value is auto-calculated from project values.
                      </DialogDescription>
                    </DialogHeader>

                    <Form {...clientForm}>
                      <form onSubmit={clientForm.handleSubmit(onCreateClient)} data-testid="create-client-form">
                        <div className="space-y-5 px-8 py-7" data-testid="create-client-fields-wrap">
                          <FormField
                            control={clientForm.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="field-label">Client Name *</FormLabel>
                                <FormControl>
                                  <Input className="field-input h-14 text-[28px]" placeholder="e.g. Meridian Finance" data-testid="create-client-name-input" {...field} />
                                </FormControl>
                                <FormMessage data-testid="create-client-name-error" />
                              </FormItem>
                            )}
                          />

                          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="create-client-industry-contact-row">
                            <FormField
                              control={clientForm.control}
                              name="industry"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="field-label">Industry</FormLabel>
                                  <FormControl>
                                    <Input className="field-input h-12" placeholder="e.g. Fintech" data-testid="create-client-industry-input" {...field} />
                                  </FormControl>
                                  <FormMessage data-testid="create-client-industry-error" />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={clientForm.control}
                              name="start_date"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="field-label">Client Since</FormLabel>
                                  <FormControl>
                                    <Input type="date" className="field-input h-12" data-testid="create-client-start-date-input" {...field} />
                                  </FormControl>
                                  <FormMessage data-testid="create-client-start-date-error" />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="create-client-finance-row">
                            <FormField
                              control={clientForm.control}
                              name="currency"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="field-label">Currency</FormLabel>
                                  <FormControl>
                                    <select className="field-select h-12" value={field.value} onChange={field.onChange} data-testid="create-client-currency-select">
                                      <option value="INR">INR</option>
                                      <option value="USD">USD</option>
                                      <option value="GBP">GBP</option>
                                      <option value="EUR">EUR</option>
                                    </select>
                                  </FormControl>
                                  <FormMessage data-testid="create-client-currency-error" />
                                </FormItem>
                              )}
                            />
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="create-client-value-auto-note">
                              Total client monthly value = sum of <strong>ACTIVE</strong> project values. Paused value is shown separately in client tables.
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="create-client-contact-row">
                            <FormField
                              control={clientForm.control}
                              name="contact_name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="field-label">Primary Contact Name</FormLabel>
                                  <FormControl>
                                    <Input className="field-input h-12" placeholder="e.g. Ruchika Mehta" data-testid="create-client-contact-name-input" {...field} />
                                  </FormControl>
                                  <FormMessage data-testid="create-client-contact-name-error" />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={clientForm.control}
                              name="contact_email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="field-label">Primary Contact Email</FormLabel>
                                  <FormControl>
                                    <Input className="field-input h-12" placeholder="contact@client.com" data-testid="create-client-contact-email-input" {...field} />
                                  </FormControl>
                                  <FormMessage data-testid="create-client-contact-email-error" />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        <DialogFooter className="border-t border-[var(--paper-200)] px-8 py-5">
                          <Button type="button" variant="outline" onClick={() => setCreateClientOpen(false)} data-testid="create-client-cancel-button">Cancel</Button>
                          <Button type="submit" data-testid="create-client-submit-button">Create Client</Button>
                        </DialogFooter>
                      </form>
                    </Form>
                  </DialogContent>
                </Dialog>

                <Button onClick={() => openProjectDialog()} data-testid="open-create-project-dialog-button">
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Project
                </Button>
              </>
            ) : null}
          </div>
        </header>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="clients-stats-strip">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`clients-stat-skeleton-${index}`} className="jump-card p-4" data-testid={`clients-stat-skeleton-${index}`}>
                <div className="skeleton-shimmer" style={{ height: 10, width: "42%", marginBottom: 10 }} />
                <div className="skeleton-shimmer" style={{ height: 34, width: "28%" }} />
              </div>
            ))
          ) : (
            <>
              <div className="jump-card p-4" data-testid="clients-stat-total-clients">
                <p className="text-xs uppercase tracking-[0.08em] text-slate-500">Total clients</p>
                <p className="mt-1 text-3xl font-semibold text-slate-900">{stats.totalClients}</p>
              </div>
              <div className="jump-card p-4" data-testid="clients-stat-active-projects">
                <p className="text-xs uppercase tracking-[0.08em] text-slate-500">Active projects</p>
                <p className="mt-1 text-3xl font-semibold text-slate-900">{stats.activeProjects}</p>
              </div>
              <div className="jump-card border border-rose-200 bg-rose-50 p-4" data-testid="clients-stat-overdue-tasks">
                <p className="text-xs uppercase tracking-[0.08em] text-rose-700">Tasks overdue</p>
                <p className="mt-1 text-3xl font-semibold text-rose-700">{stats.overdueTasks}</p>
              </div>
              <div className="jump-card border border-emerald-200 bg-emerald-50 p-4" data-testid="clients-stat-healthy-clients">
                <p className="text-xs uppercase tracking-[0.08em] text-emerald-700">Healthy clients</p>
                <p className="mt-1 text-3xl font-semibold text-emerald-700">{stats.healthyClients}</p>
              </div>
            </>
          )}
        </section>

        <section className="flex flex-wrap items-center gap-2" data-testid="clients-filters-row">
          {isLoading ? (
            <div className="flex w-full items-center gap-2" data-testid="clients-filters-skeleton-row">
              <div className="skeleton-shimmer" style={{ height: 40, width: 160, borderRadius: 10 }} />
              <div className="skeleton-shimmer" style={{ height: 40, width: 140, borderRadius: 10 }} />
              <div className="ml-auto skeleton-shimmer" style={{ height: 40, width: 270, borderRadius: 10 }} />
            </div>
          ) : (
            <>
              <select
                className="field-select h-10 w-[160px]"
                value={filters.service}
                onChange={(event) => setFilters((prev) => ({ ...prev, service: event.target.value }))}
                data-testid="clients-service-filter-select"
              >
                <option value="">All services</option>
                {[...RETAINER_SERVICE_OPTIONS, ...FIXED_SERVICE_OPTIONS].map((service) => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>

              <select
                className="field-select h-10 w-[140px]"
                value={filters.health}
                onChange={(event) => setFilters((prev) => ({ ...prev, health: event.target.value }))}
                data-testid="clients-health-filter-select"
              >
                <option value="">All health</option>
                <option value="crit">Critical</option>
                <option value="risk">At risk</option>
                <option value="warn">Watch</option>
                <option value="ok">Healthy</option>
              </select>

              <div className="relative ml-auto w-full max-w-[270px]" data-testid="clients-search-wrap">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  className="field-input h-10 pl-9"
                  placeholder="Search by client"
                  value={filters.search}
                  onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                  data-testid="clients-search-input"
                />
              </div>

              <p className="text-xs text-slate-500" data-testid="clients-count-label">{filteredSummary}</p>
            </>
          )}
        </section>

        <section data-testid="clients-table-card">
          {isLoading ? (
            <div className="card overflow-hidden" data-testid="clients-table-loading-card">
              <table className="data-table" data-testid="clients-table-loading">
                <tbody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`clients-loading-row-${index}`} data-testid={`clients-loading-row-${index}`}>
                      <td colSpan={9}>
                        <div className="skeleton-shimmer" style={{ height: 52, borderRadius: 10, marginBlock: 6 }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="card overflow-hidden" data-testid="clients-empty-card">
              <table className="data-table" data-testid="clients-table-empty">
                <tbody>
                  <tr>
                    <td colSpan={9} style={{ height: 86, textAlign: "center", color: "var(--mute-500)" }} data-testid="clients-empty-row">
                      No clients matched your filters.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={["active"]} className="space-y-3" data-testid="clients-status-accordions">
              {(["active", "paused", "archived"]).map((statusKey) => {
                const rows = clientsByStatus[statusKey] || [];
                const meta = statusKey === "active"
                  ? { title: "Active", dot: "#16A34A", headerClass: "bg-emerald-50/70" }
                  : statusKey === "paused"
                    ? { title: "Paused", dot: "#D97706", headerClass: "bg-amber-50/80" }
                    : { title: "Archived", dot: "#6B7280", headerClass: "bg-slate-100/70" };

                return (
                  <AccordionItem key={statusKey} value={statusKey} className="card overflow-hidden border border-slate-200" data-testid={`clients-accordion-${statusKey}`}>
                    <AccordionTrigger className={`px-4 py-3 hover:no-underline ${meta.headerClass}`} data-testid={`clients-accordion-trigger-${statusKey}`}>
                      <div className="flex items-center gap-2 text-left">
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.dot }} />
                        <span style={{ fontWeight: 700 }}>{meta.title}</span>
                        <span className="chip chip-mute" data-testid={`clients-accordion-count-${statusKey}`}>{rows.length}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-0" data-testid={`clients-accordion-content-${statusKey}`}>
                      {rows.length === 0 ? (
                        <div style={{ padding: 16, color: "var(--mute-500)" }} data-testid={`clients-accordion-empty-${statusKey}`}>No clients in this state.</div>
                      ) : (
                        <table className="data-table" data-testid={`clients-table-${statusKey}`}>
                          <thead>
                            <tr>
                              <th>Client</th>
                              <th>Monthly value</th>
                              <th>Services</th>
                              <th>Projects</th>
                              <th>Type</th>
                              <th>Since</th>
                              <th>Health</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((client) => {
                              const rowBand = healthBand(client.worst_health_score || 0);
                              const rowId = client.id;
                              const serviceChips = Array.isArray(client.services_assigned) ? client.services_assigned : [];
                              const clientStatus = String(client.status || "active").toLowerCase();
                              const isArchived = clientStatus === "archived";
                              const isPaused = clientStatus === "paused";
                              return (
                                <tr
                                  key={rowId}
                                  className="group"
                                  style={{
                                    cursor: "pointer",
                                    opacity: isArchived ? 0.5 : 1,
                                    background: isPaused ? "#FFF7ED" : undefined,
                                  }}
                                  onClick={() => {
                                    openClientDetailedView(client);
                                  }}
                                  data-testid={`clients-row-${rowId}`}
                                >
                                  <td data-testid={`clients-row-client-${rowId}`}>
                                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{client.name}</div>
                                    <div style={{ fontSize: 12, color: "var(--mute-500)", marginTop: 2 }}>{client.industry}</div>
                                  </td>
                                  <td className="tabular" data-testid={`clients-row-monthly-${rowId}`}>
                                    <div data-testid={`clients-row-monthly-active-${rowId}`}>
                                      <strong>{getCurrencySymbol(client.currency)}{Number(client.active_monthly_value || client.monthly_retainer_value || 0).toLocaleString("en-IN")}</strong>
                                      <div style={{ fontSize: 11.5, color: "var(--mute-400)" }}>Active / month</div>
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--mute-500)" }} data-testid={`clients-row-monthly-paused-${rowId}`}>
                                      Paused: {getCurrencySymbol(client.currency)}{Number(client.paused_monthly_value || 0).toLocaleString("en-IN")}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: "var(--mute-500)" }} data-testid={`clients-row-monthly-combined-${rowId}`}>
                                      Combined: {getCurrencySymbol(client.currency)}{Number(client.combined_monthly_value || client.monthly_retainer_value || 0).toLocaleString("en-IN")}
                                    </div>
                                  </td>
                                  <td data-testid={`clients-row-services-${rowId}`}>
                                    <div className="flex flex-wrap items-center gap-1">
                                      {serviceChips.map((service) => (
                                        <span key={service} className={chipForService(service)} style={{ fontSize: 11 }}>{service}</span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="tabular" style={{ color: "var(--mute-600)" }} data-testid={`clients-row-project-count-${rowId}`}>{client.project_count || 0}</td>
                                  <td style={{ color: "var(--mute-600)" }} data-testid={`clients-row-type-${rowId}`}>{client.project_type_label || "Retainer"}</td>
                                  <td style={{ color: "var(--mute-500)", fontSize: 12 }} data-testid={`clients-row-since-${rowId}`}>{formatDateLabel(client.start_date)}</td>
                                  <td data-testid={`clients-row-health-${rowId}`}>
                                    <span className={healthChipClasses[rowBand]}>
                                      <span className={healthDotClasses[rowBand]} />
                                      {client.worst_health_score || 0} {healthLabel[rowBand]}
                                    </span>
                                    {Number(client.overdue_tasks || 0) > 0 ? (
                                      <span className="chip chip-crit" style={{ fontSize: 11, marginLeft: 4 }} data-testid={`clients-row-overdue-chip-${rowId}`}>
                                        {client.overdue_tasks} overdue
                                      </span>
                                    ) : null}
                                  </td>
                                  <td className="text-right" data-testid={`clients-row-view-button-wrap-${rowId}`}>
                                    <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100" data-testid={`clients-row-actions-${rowId}`}>
                                      <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openClientDetailedView(client);
                                        }}
                                        data-testid={`clients-row-view-projects-button-${rowId}`}
                                      >
                                        Open client view →
                                      </button>

                                      {user.role === "super_admin" && clientStatus !== "archived" ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm"
                                          onClick={(event) => (clientStatus === "paused" ? handleResumeClient(event, client) : openPauseClientModal(event, client))}
                                          data-testid={`clients-row-pause-resume-button-${rowId}`}
                                        >
                                          {clientStatus === "paused" ? "Resume" : "Pause"}
                                        </button>
                                      ) : null}

                                      {user.role === "super_admin" && clientStatus !== "archived" ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm"
                                          onClick={(event) => openArchiveClientModal(event, client)}
                                          data-testid={`clients-row-archive-button-${rowId}`}
                                        >
                                          Archive
                                        </button>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </section>

        <Dialog open={clientPauseModal.open} onOpenChange={(open) => setClientPauseModal((prev) => ({ ...prev, open }))}>
          <DialogContent className="sm:max-w-[560px]" data-testid="clients-pause-modal">
            <DialogHeader>
              <DialogTitle data-testid="clients-pause-modal-title">Pause client</DialogTitle>
              <DialogDescription data-testid="clients-pause-modal-subtitle">{clientPauseModal.client?.name || "Client"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] leading-6 text-amber-900" data-testid="clients-pause-modal-impact-note">
                <strong>Pauses ALL active projects for this client.</strong><br />
                Active projects move to Paused and are excluded from active scorecards.
              </div>
              <label className="field-label">Reason *</label>
              <select className="field-select" value={clientPauseForm.reason} onChange={(event) => setClientPauseForm((prev) => ({ ...prev, reason: event.target.value }))} data-testid="clients-pause-reason-select">
                <option value="">Select reason...</option>
                {clientPauseReasons.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <label className="field-label">Notes (optional)</label>
              <Textarea value={clientPauseForm.notes} onChange={(event) => setClientPauseForm((prev) => ({ ...prev, notes: event.target.value }))} data-testid="clients-pause-notes-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClientPauseModal({ open: false, client: null })} data-testid="clients-pause-cancel-button">Cancel</Button>
              <Button onClick={confirmPauseClient} disabled={isLifecycleSaving} style={{ background: "#D97706", borderColor: "#D97706" }} data-testid="clients-pause-confirm-button">
                {isLifecycleSaving ? "Pausing..." : "Pause client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={clientArchiveModal.open} onOpenChange={(open) => setClientArchiveModal((prev) => ({ ...prev, open }))}>
          <DialogContent className="sm:max-w-[560px]" data-testid="clients-archive-modal">
            <DialogHeader>
              <DialogTitle className="text-rose-700" data-testid="clients-archive-modal-title">Archive client</DialogTitle>
              <DialogDescription data-testid="clients-archive-modal-subtitle">{clientArchiveModal.client?.name || "Client"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[13px] leading-6 text-rose-800" data-testid="clients-archive-modal-warning-note">
                <strong>Archives this client and all linked projects.</strong><br />
                This removes them from active operations views.
              </div>
              <label className="field-label">Reason *</label>
              <select className="field-select" value={clientArchiveForm.reason} onChange={(event) => setClientArchiveForm((prev) => ({ ...prev, reason: event.target.value }))} data-testid="clients-archive-reason-select">
                <option value="">Select reason...</option>
                {clientArchiveReasons.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <label className="field-label">Type client name to confirm *</label>
              <Input value={clientArchiveForm.confirm_name} onChange={(event) => setClientArchiveForm((prev) => ({ ...prev, confirm_name: event.target.value }))} placeholder={clientArchiveModal.client?.name || "Client name"} data-testid="clients-archive-confirm-name-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClientArchiveModal({ open: false, client: null })} data-testid="clients-archive-cancel-button">Cancel</Button>
              <Button onClick={confirmArchiveClient} disabled={!archiveClientNameMatched || isLifecycleSaving} style={{ background: archiveClientNameMatched ? "#B91C1C" : "#FEE2E2", borderColor: archiveClientNameMatched ? "#B91C1C" : "#FECACA", color: archiveClientNameMatched ? "#fff" : "#EF9A9A" }} data-testid="clients-archive-confirm-button">
                {isLifecycleSaving ? "Archiving..." : "Archive client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <button
          type="button"
          aria-label="Close client drawer"
          className={`modal-backdrop ${isDrawerOpen ? "open" : ""}`}
          style={{ border: "none", zIndex: 60, cursor: "default" }}
          onClick={closeClientDrawer}
          data-testid="client-projects-drawer-backdrop"
        />

        <aside className={`task-drawer ${isDrawerOpen ? "open" : ""}`} style={{ width: 560, zIndex: 65 }} data-testid="client-projects-drawer">
          <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--paper-200)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "#fff" }} data-testid="client-projects-drawer-header">
            <button type="button" className="btn btn-sm" onClick={closeClientDrawer} data-testid="client-projects-drawer-close-button">
              <X className="h-4 w-4" />
            </button>
            <div style={{ flex: 1, minWidth: 0 }} data-testid="client-projects-drawer-title-wrap">
              <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }} data-testid="client-projects-drawer-client-name">{activeClient?.name || "Client Name"}</div>
              <div style={{ fontSize: 12.5, color: "var(--mute-500)", marginTop: 2 }} data-testid="client-projects-drawer-client-industry">{activeClient?.industry || ""}</div>
            </div>
            {canCreatePortfolio ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => openProjectDialog({ clientId: activeClientId, lockClient: true })}
                data-testid="client-projects-drawer-add-project-button"
              >
                + Add Project
              </button>
            ) : null}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderBottom: "1px solid var(--paper-200)", flexShrink: 0, background: "var(--paper-50)" }} data-testid="client-projects-drawer-stats-strip">
            <div style={{ padding: "14px 18px", borderRight: "1px solid var(--paper-200)" }} data-testid="client-projects-drawer-stat-retainer">
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--mute-400)", marginBottom: 4 }}>Active monthly value</div>
              <div style={{ fontSize: 18, fontWeight: 700 }} data-testid="client-projects-drawer-stat-active-value">
                {activeClient ? `${getCurrencySymbol(activeClient.currency)}${Number(activeClient.active_monthly_value || activeClient.monthly_retainer_value || 0).toLocaleString("en-IN")}` : "—"}
              </div>
              <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--mute-500)" }} data-testid="client-projects-drawer-stat-paused-value">
                Paused: {activeClient ? `${getCurrencySymbol(activeClient.currency)}${Number(activeClient.paused_monthly_value || 0).toLocaleString("en-IN")}` : "—"}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--mute-500)" }} data-testid="client-projects-drawer-stat-combined-value">
                Combined: {activeClient ? `${getCurrencySymbol(activeClient.currency)}${Number(activeClient.combined_monthly_value || activeClient.monthly_retainer_value || 0).toLocaleString("en-IN")}` : "—"}
              </div>
            </div>
            <div style={{ padding: "14px 18px", borderRight: "1px solid var(--paper-200)" }} data-testid="client-projects-drawer-stat-project-count">
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--mute-400)", marginBottom: 4 }}>Active projects</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{activeClient?.active_projects || 0}</div>
            </div>
            <div style={{ padding: "14px 18px" }} data-testid="client-projects-drawer-stat-health">
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--mute-400)", marginBottom: 4 }}>Combined health</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{activeClient?.average_health_score || 0}</div>
            </div>
          </div>

          <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--paper-200)", flexShrink: 0, display: "flex", alignItems: "center", gap: 8, background: "#fff" }} data-testid="client-projects-drawer-contact-row">
            <p style={{ fontSize: 12.5, color: "var(--mute-600)" }} data-testid="client-projects-drawer-contact-text">
              {activeClient?.contact_name || "Primary contact not set"}
              {activeClient?.contact_email ? ` · ${activeClient.contact_email}` : ""}
              {activeClient?.start_date ? ` · Since ${formatDateLabel(activeClient.start_date)}` : ""}
            </p>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }} data-testid="client-projects-drawer-project-list">
            <div className="rounded-2xl border border-slate-200 bg-white p-3" style={{ marginBottom: 14 }} data-testid="client-projects-drawer-billing-breakup-card">
              <div className="flex items-center justify-between gap-2" data-testid="client-projects-drawer-billing-breakup-header">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="client-projects-drawer-billing-breakup-title">Client billing breakup</p>
                <span className="chip chip-brand" data-testid="client-projects-drawer-billing-breakup-total">Total {activeClient ? `${getCurrencySymbol(activeClient.currency)}${Math.round(drawerBillingBreakdownTotal).toLocaleString("en-IN")}` : "—"}/mo</span>
              </div>
              <div className="mt-2 space-y-1" data-testid="client-projects-drawer-billing-breakup-rows">
                {drawerBillingBreakdownRows.length ? (
                  drawerBillingBreakdownRows.map((row) => (
                    <div key={row.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`client-projects-drawer-billing-breakup-row-${row.id}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900" data-testid={`client-projects-drawer-billing-breakup-project-${row.id}`}>{row.name}</p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <span className="text-xs text-slate-500" data-testid={`client-projects-drawer-billing-breakup-service-${row.id}`}>{row.service_type}</span>
                          <span className={`chip ${row.status === "active" ? "chip-ok" : row.status === "paused" ? "chip-warn" : row.status === "completed" ? "chip-brand" : "chip-crit"}`} style={{ fontSize: 11 }} data-testid={`client-projects-drawer-billing-breakup-status-${row.id}`}>{row.status}</span>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-slate-900" data-testid={`client-projects-drawer-billing-breakup-value-${row.id}`}>{activeClient ? `${getCurrencySymbol(activeClient.currency)}${Math.round(row.value).toLocaleString("en-IN")}` : `₹${Math.round(row.value).toLocaleString("en-IN")}`}/mo</p>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-500" data-testid="client-projects-drawer-billing-breakup-empty">No projects yet.</p>
                )}
              </div>
            </div>

            {isDrawerLoading ? (
              <p className="text-sm text-slate-500" data-testid="client-projects-drawer-loading-text">Loading projects...</p>
            ) : clientProjects.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="client-projects-drawer-empty-text">No projects for this client yet.</p>
            ) : (
              <div className="space-y-3" data-testid="client-projects-drawer-project-cards-wrap">
                {clientProjects.map((project) => {
                  const score = Number(project.health_score || 0);
                  const color = ringStrokeColor(score);
                  const ringLength = 2 * Math.PI * 14;
                  const ringOffset = ringLength * (1 - Math.min(100, score) / 100);
                  const isMember = Boolean(project.member);
                  const teamEmails = (project.team_emails || []).slice(0, 4);

                  return (
                    <div key={project.id} className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`client-projects-drawer-project-card-${project.id}`}>
                      <div className="flex items-start gap-3" data-testid={`client-projects-drawer-project-card-header-${project.id}`}>
                        <div style={{ width: 44, height: 44, borderRadius: 999, border: "1px solid var(--paper-200)", display: "grid", placeItems: "center", position: "relative" }} data-testid={`client-projects-drawer-project-health-ring-${project.id}`}>
                          <svg width="36" height="36" viewBox="0 0 36 36" className="gauge-ring">
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                            <circle cx="18" cy="18" r="14" fill="none" stroke={color} strokeWidth="3" strokeDasharray={ringLength} strokeDashoffset={ringOffset} strokeLinecap="round" transform="rotate(-90 18 18)" />
                          </svg>
                          <span style={{ position: "absolute", fontSize: 10, fontWeight: 700, color }} data-testid={`client-projects-drawer-project-health-score-${project.id}`}>{score}</span>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className="truncate text-sm font-semibold text-slate-900" data-testid={`client-projects-drawer-project-name-${project.id}`}>{project.name}</p>
                          <p className="text-xs text-slate-500" data-testid={`client-projects-drawer-project-meta-${project.id}`}>
                            {project.id.slice(0, 8).toUpperCase()} · Admin: {project.project_admin_name || project.project_admin_email}
                          </p>
                          <p className="mt-0.5 text-xs font-semibold text-slate-700" data-testid={`client-projects-drawer-project-value-${project.id}`}>
                            {activeClient ? `${getCurrencySymbol(activeClient.currency)}${Number(project.project_value_monthly || 0).toLocaleString("en-IN")}` : `₹${Number(project.project_value_monthly || 0).toLocaleString("en-IN")}`}/mo
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2" data-testid={`client-projects-drawer-project-badges-${project.id}`}>
                            <span className={chipForService(project.service_type)} style={{ fontSize: 11 }} data-testid={`client-projects-drawer-project-category-chip-${project.id}`}>{project.service_type}</span>
                            <span className={chipForBillingType(project.billing_type || project.project_type)} style={{ fontSize: 11 }} data-testid={`client-projects-drawer-project-billing-chip-${project.id}`}>
                              {String(project.billing_type || project.project_type || "retainer").toUpperCase()}
                            </span>
                            {!isMember ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`client-projects-drawer-project-viewer-badge-${project.id}`}>
                                <Lock className="h-3 w-3" /> Viewer
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-2" data-testid={`client-projects-drawer-project-footer-${project.id}`}>
                        <div>
                          <div className="flex items-center -space-x-2" data-testid={`client-projects-drawer-project-team-avatars-${project.id}`}>
                            {teamEmails.map((email, index) => (
                              <div key={`${project.id}-${email}`} className="avatar avatar-sm avatar-a border-2 border-white" style={{ zIndex: teamEmails.length - index }} data-testid={`client-projects-drawer-project-team-avatar-${project.id}-${index}`}>
                                {initialsFromEmail(email)}
                              </div>
                            ))}
                          </div>
                          <div className="mt-1 text-xs text-slate-500" data-testid={`client-projects-drawer-project-task-summary-${project.id}`}>
                            {project.open_tasks_count || 0} open tasks
                            {Number(project.overdue_tasks_count || 0) > 0 ? (
                              <span className="chip chip-crit" style={{ fontSize: 11, marginLeft: 6 }}>
                                {project.overdue_tasks_count} overdue
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            navigate(`/project/${project.id}`);
                            closeClientDrawer();
                          }}
                          data-testid={`client-projects-drawer-project-open-${project.id}`}
                        >
                          {isMember ? "Open project →" : "View (read only) →"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {user.role === "super_admin" ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-3" style={{ marginTop: 14 }} data-testid="client-projects-drawer-recent-activity-card">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2"
                  style={{ border: "none", background: "transparent", padding: 0, textAlign: "left" }}
                  onClick={() => setIsClientAuditExpanded((prev) => !prev)}
                  data-testid="client-projects-drawer-recent-activity-toggle"
                >
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="client-projects-drawer-recent-activity-title">Recent Activity</p>
                    <p className="text-xs text-slate-500" data-testid="client-projects-drawer-recent-activity-subtitle">Last 10 audit events for this client</p>
                  </div>
                  <span className="chip chip-mute" data-testid="client-projects-drawer-recent-activity-count-chip">{clientAuditRows.length}</span>
                </button>

                {isClientAuditExpanded ? (
                  <div className="mt-3 space-y-2" data-testid="client-projects-drawer-recent-activity-list">
                    {clientAuditRows.length ? clientAuditRows.map((row) => (
                      <div key={row.id} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`client-projects-drawer-recent-activity-row-${row.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900" data-testid={`client-projects-drawer-recent-activity-action-${row.id}`}>{row.action}</p>
                          <span className={`chip ${row.status === "success" ? "chip-ok" : "chip-crit"}`} data-testid={`client-projects-drawer-recent-activity-status-${row.id}`}>{row.status}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500" data-testid={`client-projects-drawer-recent-activity-meta-${row.id}`}>
                          {(row.actor?.email || row.actor?.name || "system")} · {new Date(row.created_at).toLocaleString("en-IN")}
                        </p>
                      </div>
                    )) : <p className="text-xs text-slate-500" data-testid="client-projects-drawer-recent-activity-empty">No recent events.</p>}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </aside>

        <Dialog open={createProjectOpen} onOpenChange={(open) => (!open ? closeProjectDialog() : setCreateProjectOpen(true))}>
          <DialogContent className="max-h-[92vh] overflow-y-auto p-0 sm:max-w-[940px]" data-testid="create-project-dialog">
            <DialogHeader className="border-b border-[var(--paper-200)] px-8 py-6">
              <DialogTitle className="text-[52px] leading-none tracking-[-0.02em]" data-testid="create-project-title">Create Project</DialogTitle>
              <DialogDescription className="text-[22px] text-[var(--mute-500)]" data-testid="create-project-description">
                One service per project. Client can have multiple projects.
              </DialogDescription>
            </DialogHeader>

            <Form {...projectForm}>
              <form onSubmit={projectForm.handleSubmit(onCreateProject)} data-testid="create-project-form">
                <div className="space-y-5 px-8 py-7" data-testid="create-project-fields-wrap">
                  <FormField
                    control={projectForm.control}
                    name="client_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="field-label">Client *</FormLabel>
                        <FormControl>
                          <select className="field-select h-12" value={field.value} onChange={field.onChange} disabled={lockProjectClientField} data-testid="create-project-client-select">
                            <option value="">Select client...</option>
                            {activeClients.map((client) => (
                              <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                          </select>
                        </FormControl>
                        {lockProjectClientField ? (
                          <p className="text-xs text-indigo-600" data-testid="create-project-client-locked-note">Client pre-selected from drawer.</p>
                        ) : null}
                        <FormMessage data-testid="create-project-client-error" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={projectForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="field-label">Project Name *</FormLabel>
                        <FormControl>
                          <Input className="field-input h-12" placeholder="e.g. Meridian Finance — SEO" data-testid="create-project-name-input" {...field} />
                        </FormControl>
                        <FormMessage data-testid="create-project-name-error" />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <FormField
                      control={projectForm.control}
                      name="project_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="field-label">Billing Type *</FormLabel>
                          <FormControl>
                            <select className="field-select h-12" value={field.value} onChange={field.onChange} data-testid="create-project-type-select">
                              <option value="retainer">RETAINER</option>
                              <option value="fixed">FIXED</option>
                            </select>
                          </FormControl>
                          <FormMessage data-testid="create-project-type-error" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={projectForm.control}
                      name="service_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="field-label">Project Category *</FormLabel>
                          <FormControl>
                            <select className="field-select h-12" value={field.value} onChange={field.onChange} data-testid="create-project-service-select">
                              <option value="">Select category...</option>
                              {selectedServiceOptions.map((service) => (
                                <option key={service} value={service}>{service}</option>
                              ))}
                            </select>
                          </FormControl>
                          <FormMessage data-testid="create-project-service-error" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <FormField
                      control={projectForm.control}
                      name="visibility_scope"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="field-label">Project Access</FormLabel>
                          <FormControl>
                            <select className="field-select h-12" value={field.value} onChange={field.onChange} data-testid="create-project-visibility-select">
                              <option value="workspace_only">Workspace only (recommended)</option>
                              <option value="shared_both">Shared — both workspaces</option>
                            </select>
                          </FormControl>
                          <FormMessage data-testid="create-project-visibility-error" />
                        </FormItem>
                      )}
                    />

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="create-project-manager-autostamp-note">
                      Project manager is auto-assigned from your profile. Team members can be added from anywhere in your organization (now or later from Project view).
                    </div>
                  </div>

                  {selectedProjectType === "fixed" ? (
                    <FormField
                      control={projectForm.control}
                      name="project_deadline"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="field-label">Project Deadline</FormLabel>
                          <FormControl>
                            <Input type="date" className="field-input h-12" data-testid="create-project-deadline-input" {...field} />
                          </FormControl>
                          <FormMessage data-testid="create-project-deadline-error" />
                        </FormItem>
                      )}
                    />
                  ) : (
                    <FormField
                      control={projectForm.control}
                      name="retainer_start_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="field-label">Retainer Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" className="field-input h-12" data-testid="create-project-retainer-start-input" {...field} />
                          </FormControl>
                          <FormMessage data-testid="create-project-retainer-start-error" />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <DialogFooter className="border-t border-[var(--paper-200)] px-8 py-5">
                  <Button type="button" variant="outline" onClick={closeProjectDialog} data-testid="create-project-cancel-button">Cancel</Button>
                  <Button type="submit" data-testid="create-project-submit-button">Create Project</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </section>
    </AppShell>
  );
}
